/**
 * RanchDocs document upload — the actual upload, not a metadata-only stub.
 *
 * The previous version of this page took a fake filename / size / mime,
 * never opened a file picker, and POSTed to a non-existent /api/docs
 * route. The page now drives the real signed-upload flow:
 *
 *   1. POST /api/docs/upload-url → { url, key }
 *      The url has a short-lived HMAC token baked into the path.
 *   2. PUT  <url> with the raw file bytes (Content-Type matches mime).
 *      Cannot use authFetch — that JSON-encodes everything.
 *   3. POST /api/docs/upload-confirm with the metadata + r2_key.
 *      Returns the new document row and enqueues OCR.
 *   4. Optional: POST /api/docs/bulk { action: 'tag', documentIds, tagIds }
 *      to attach selected tags (upload-confirm doesn't accept tag_ids).
 *
 * Client-side validation matches the worker (ALLOWED_DOC_MIMES, 50 MB).
 *
 * 2026-05-07 fix — taxonomy GETs were calling `/api/taxonomy/*`, which
 * 404s on the post-audit server (taxonomy lives at /api/docs/taxonomy/*).
 * The catch swallowed the error so the page rendered with empty Type /
 * Correspondent / Storage Location dropdowns and a Tags section saying
 * "No tags available". Switched to the canonical paths.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, ChevronLeft, ChevronRight, Check, X, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { DocType, Correspondent, Tag, StorageLocation } from "@shared/types";

const STEPS = ["Choose File", "Metadata", "Review & Upload"];

const ALLOWED_MIMES = new Set([
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const ACCEPT_ATTR = Array.from(ALLOWED_MIMES).join(",");
const MAX_BYTES = 50 * 1024 * 1024;

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {STEPS.map((label, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isComplete = stepNum < currentStep;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <div
                className={cn(
                  "h-0.5 w-12 sm:w-20 mx-1",
                  isComplete ? "bg-ranch-gold" : "bg-border",
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors",
                  isActive && "bg-ranch-gold text-white",
                  isComplete && "bg-ranch-gold text-white",
                  !isActive && !isComplete && "bg-muted text-muted-foreground border border-border",
                )}
              >
                {isComplete ? <Check className="h-4 w-4" /> : stepNum}
              </div>
              <span
                className={cn(
                  "text-2xs whitespace-nowrap",
                  isActive ? "text-foreground font-medium" : "text-muted-foreground",
                )}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function formatBytes(n: number): string {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: File): string | null {
  if (file.size > MAX_BYTES) {
    return `File exceeds 50 MB limit (${formatBytes(file.size)}).`;
  }
  // file.type can be empty for some browsers/extensions — check by name
  // suffix as a fallback so the user isn't blocked from valid uploads.
  const mime = file.type || guessMimeFromName(file.name);
  if (!ALLOWED_MIMES.has(mime)) {
    return `Unsupported file type (${file.type || file.name}). Allowed: PDF, Word (.doc/.docx), JPEG, PNG, WebP, HEIC.`;
  }
  return null;
}

function guessMimeFromName(name: string): string {
  const ext = name.toLowerCase().slice(name.lastIndexOf("."));
  switch (ext) {
    case ".pdf": return "application/pdf";
    case ".jpg":
    case ".jpeg": return "image/jpeg";
    case ".png": return "image/png";
    case ".webp": return "image/webp";
    case ".heic": return "image/heic";
    case ".doc": return "application/msword";
    case ".docx": return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    default: return "";
  }
}

export function DocumentUploadPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Taxonomy data
  const [types, setTypes] = useState<DocType[]>([]);
  const [correspondents, setCorrespondents] = useState<Correspondent[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Step 1 — the actual file the user picks.
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Step 2 fields
  const [title, setTitle] = useState("");
  const [typeId, setTypeId] = useState("");
  const [corrId, setCorrId] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [storageId, setStorageId] = useState("");
  const [dateReceived, setDateReceived] = useState(() => new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const typeMap = useMemo(() => new Map(types.map((t) => [t.id, t.name])), [types]);
  const corrMap = useMemo(() => new Map(correspondents.map((c) => [c.id, c.name])), [correspondents]);
  const tagMap = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);
  const slMap = useMemo(() => new Map(storageLocations.map((s) => [s.id, s.name])), [storageLocations]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Canonical taxonomy surface is /api/docs/taxonomy/* (audit §1).
        const [tData, cData, tagData, slData] = await Promise.all([
          authFetch<{ items: DocType[] }>("/api/docs/taxonomy/types"),
          authFetch<{ items: Correspondent[] }>("/api/docs/taxonomy/correspondents"),
          authFetch<{ items: Tag[] }>("/api/docs/taxonomy/tags"),
          authFetch<{ items: StorageLocation[] }>("/api/docs/taxonomy/storage-locations"),
        ]);
        if (!cancelled) {
          setTypes(tData.items ?? []);
          setCorrespondents(cData.items ?? []);
          setTags(tagData.items ?? []);
          setStorageLocations(slData.items ?? []);
        }
      } catch (err: unknown) {
        console.error("[DocUpload taxonomy]", err);
      } finally {
        if (!cancelled) setTaxonomyLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const acceptFile = (next: File): void => {
    const err = validateFile(next);
    if (err) {
      toast.error(err);
      return;
    }
    setFile(next);
    // Pre-populate the title from the filename if the user hasn't typed
    // anything yet — strip the extension to keep it clean.
    if (!title.trim()) {
      const base = next.name.includes(".") ? next.name.slice(0, next.name.lastIndexOf(".")) : next.name;
      setTitle(base);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) acceptFile(f);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const toggleTag = (tagId: string) => {
    setTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const canProceedStep1 = file !== null;
  const canProceedStep2 = title.trim().length > 0;

  const handleSubmit = async () => {
    if (!file) {
      toast.error("No file selected.");
      return;
    }
    setSubmitting(true);
    try {
      // 1. Reserve a signed upload URL.
      const reserved = await authFetch<{ url: string; key: string }>(
        "/api/docs/upload-url",
        {
          method: "POST",
          body: JSON.stringify({
            filename: file.name,
            mime_type: file.type || guessMimeFromName(file.name),
            file_size: file.size,
          }),
        },
      );

      // 2. PUT the bytes directly. authFetch always JSON-encodes, so we
      // bypass it here.
      const putRes = await fetch(reserved.url, {
        method: "PUT",
        headers: { "Content-Type": file.type || guessMimeFromName(file.name) },
        body: file,
        credentials: "include",
      });
      if (!putRes.ok) {
        const text = await putRes.text().catch(() => "");
        throw new Error(`Upload failed (${putRes.status}): ${text || putRes.statusText}`);
      }

      // 3. Confirm — writes D1 row and enqueues OCR.
      const confirmed = await authFetch<{ id: string }>(
        "/api/docs/upload-confirm",
        {
          method: "POST",
          body: JSON.stringify({
            title: title.trim(),
            r2_key: reserved.key,
            mime_type: file.type || guessMimeFromName(file.name),
            file_size: file.size,
            document_type_id: typeId || undefined,
            correspondent_id: corrId || undefined,
            storage_location_id: storageId || undefined,
            date_on_document: dateReceived || undefined,
          }),
        },
      );

      // 4. Apply tags via bulk tag (upload-confirm doesn't accept them).
      if (tagIds.length > 0 && confirmed?.id) {
        await authFetch("/api/docs/bulk", {
          method: "POST",
          body: JSON.stringify({
            action: "tag",
            documentIds: [confirmed.id],
            tagIds,
          }),
        });
      }

      toast.success("Document uploaded — OCR is running in the background");
      navigate("/documents");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to upload document");
      console.error("[DocUpload submit]", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Upload Document"
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Documents", href: "/documents" },
          { label: "Upload" },
        ]}
      />
      <StepIndicator currentStep={currentStep} />
      {taxonomyLoading ? (
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </Card>
      ) : (
        <>
          {/* Step 1 — real file picker */}
          {currentStep === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileUp className="h-5 w-5 text-ranch-gold" /> Choose File
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  onChange={handleFileInput}
                  className="hidden"
                  aria-hidden="true"
                />
                {file ? (
                  <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{file.name}</p>
                      <p className="text-2xs text-muted-foreground">
                        {formatBytes(file.size)} · {file.type || guessMimeFromName(file.name) || "unknown type"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => inputRef.current?.click()}
                      >
                        Replace
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setFile(null)}
                        aria-label="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    className={cn(
                      "flex w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 text-center transition-colors",
                      dragOver
                        ? "border-ranch-gold bg-ranch-gold/5"
                        : "border-border hover:border-ranch-gold hover:bg-muted/30",
                    )}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Drop a file here, or click to choose</p>
                      <p className="text-2xs text-muted-foreground mt-1">
                        PDF, Word, or image · up to 50 MB
                      </p>
                    </div>
                  </button>
                )}
              </CardContent>
            </Card>
          )}
          {/* Step 2 — Metadata */}
          {currentStep === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Document Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="up-title">Title *</Label>
                  <Input
                    id="up-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ranch Insurance Policy 2024"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Document Type</Label>
                    <Select value={typeId} onValueChange={setTypeId}>
                      <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                      <SelectContent>
                        {types.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Correspondent</Label>
                    <Select value={corrId} onValueChange={setCorrId}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {correspondents.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-3 p-3 border rounded-md bg-muted/30">
                    {tags.map((tag) => (
                      <label key={tag.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={tagIds.includes(tag.id)}
                          onCheckedChange={() => toggleTag(tag.id)}
                        />
                        {tag.name}
                      </label>
                    ))}
                    {tags.length === 0 && (
                      <span className="text-sm text-muted-foreground">No tags available</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Storage Location</Label>
                    <Select value={storageId} onValueChange={setStorageId}>
                      <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {storageLocations.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="up-date">Date on Document</Label>
                    <Input
                      id="up-date"
                      type="date"
                      value={dateReceived}
                      onChange={(e) => setDateReceived(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="up-notes">Notes (informational)</Label>
                  <Textarea
                    id="up-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Notes for your own reference (not stored on the document record)."
                  />
                </div>
              </CardContent>
            </Card>
          )}
          {/* Step 3 — Review */}
          {currentStep === 3 && (
            <Card>
              <CardHeader>
                <CardTitle>Review & Upload</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Confirm the file and metadata, then upload. OCR runs automatically in the background.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Title</p>
                    <p className="font-medium">{title || "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">File</p>
                    <p className="font-medium font-mono text-sm">{file?.name ?? "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Size</p>
                    <p className="font-medium">{file ? formatBytes(file.size) : "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">MIME Type</p>
                    <p className="font-medium">{file?.type || (file ? guessMimeFromName(file.name) : "\u2014")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Document Type</p>
                    <p className="font-medium">{typeMap.get(typeId) || "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Correspondent</p>
                    <p className="font-medium">{corrMap.get(corrId) || "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Storage Location</p>
                    <p className="font-medium">{slMap.get(storageId) || "\u2014"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date on Document</p>
                    <p className="font-medium">{dateReceived || "\u2014"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Tags</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tagIds.length > 0 ? (
                        tagIds.map((tid) => (
                          <Badge key={tid} variant="outline" className="text-2xs">
                            {tagMap.get(tid) || tid}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>
                  {notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm">{notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
              disabled={currentStep === 1 || submitting}
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {currentStep < 3 ? (
              <Button
                onClick={() => setCurrentStep((s) => Math.min(3, s + 1))}
                disabled={
                  (currentStep === 1 && !canProceedStep1) ||
                  (currentStep === 2 && !canProceedStep2)
                }
                className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting || !file}
                className="bg-ranch-gold hover:bg-ranch-gold/90 text-white"
              >
                {submitting ? "Uploading\u2026" : "Upload Document"}
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
}
